import localeNL from '@angular/common/locales/nl';
import { inject, Injectable, LOCALE_ID, NgModule, provideAppInitializer } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { BrowserModule, Title } from '@angular/platform-browser';
import { CommonModule, registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule, HttpHandler, HttpRequest } from '@angular/common/http';
import { RouterModule, RouterStateSnapshot, TitleStrategy } from '@angular/router';

import { NgxsModule, Store } from '@ngxs/store';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { VariantTranslateLoader } from './core/i18n/variant-translate-loader';

import { MessageModule } from 'primeng/message';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { DialogModule } from 'primeng/dialog';

import {
  AppState,
  DeelnemersState,
  DownloadsState,
  FeatureflagsState,
  GenericState,
  LeerkrachtenState,
  OnderwijsLocatiesState,
  SchooladviezenState,
  SettingsState,
  ToetsenState,
  UiState,
} from '@store/states';
import { BffForbiddenInterceptor } from '@interceptors/bff-forbidden.interceptor';
import { DoeToastComponent } from '@shared/components/toast/doe-toast.component';
import {
  PrintStartcodesLayoutComponent,
} from '@shared/components/print-startcodes-layout/print-startcodes-layout.component';

import { AppComponent } from './app.component';
import { LayoutComponent } from './layout/layout.component';
import { appRoutes } from './app.routes';
import { environment } from '@environments/environment';
import { providePrimeNG } from 'primeng/config';
import { ButtonModule } from 'primeng/button';
import { CitoDoePreset } from '../Cito.Doe.PrimeNG.theme';
import { CitoOcwPreset } from '../Cito.Ocw.PrimeNG.theme';
import { SetAppVariant } from '@store/actions/generic.actions';
import { AppSettings } from '@models/generic';
import { AppSettingsService } from '@services/app-settings.service';
import { AuthenticationModule } from '@features/auth/authentication.module';
import { AppVariants } from '@store/models';
import { CaptureUIComponent } from '../../msw/capture/capture-ui.component';
import { CaptureInterceptor } from '../../msw/capture/capture-interceptor';
import { CaptureStorage } from '../../msw/capture/capture-storage';
import { CaptureModeService } from '../../msw/capture/capture-mode.service';

registerLocaleData(localeNL);

export function appInitializerFactory(translate: TranslateService) {
  return () => {
    translate.setDefaultLang('nl');
    return translate.use('nl');
  };
}

export function appSettingsInitializerFactory(appSettingsService: AppSettingsService, store: Store) {
  return () => {
    const settings: AppSettings = appSettingsService.appSettings;
    if (settings?.AppVariant) {
      store.dispatch(new SetAppVariant(settings.AppVariant));
    }
  };
}



export function HttpLoaderFactory(http: HttpClient) {
  // Load base translations from nl.json and merge with variant-specific overrides
  // (nl-doe.json or nl-ocw.json based on environment.variant)
  return new VariantTranslateLoader(
    http,
    './assets/i18n/',
    '.json',
    environment.variant
  );
}

@Injectable()
export class TemplatePageTitleStrategy extends TitleStrategy {
  private readonly appPrefix: string;

  constructor(private readonly title: Title) {
    super();
    this.appPrefix = environment.variant === AppVariants.OCW ? 'OCW doorstroomtoets' : 'DOE';
  }

  override updateTitle(routerState: RouterStateSnapshot) {
    const title = this.buildTitle(routerState) as string;
    let newTitle = '';

    if (title !== undefined) {
      if (title) {
        const words = title.split(' ');
        newTitle = words
          .map((word) => {
            return word[0].toUpperCase() + word.substring(1);
          })
          .join(' ');
      }
      this.title.setTitle(`${this.appPrefix} ${newTitle}`);
    }
  }
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    CommonModule,
    RouterModule.forRoot(appRoutes, {
      paramsInheritanceStrategy: 'always',
      initialNavigation: 'enabledNonBlocking',
    }),
    TranslateModule.forRoot({
      defaultLanguage: 'nl',
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    ButtonModule,
    LayoutComponent,
    AuthenticationModule,
    CaptureUIComponent,
    NgxsModule.forRoot([
      AppState,
      UiState,
      GenericState,
      DeelnemersState,
      SettingsState,
      ToetsenState,
      LeerkrachtenState,
      OnderwijsLocatiesState,
      SchooladviezenState,
      DownloadsState,
      FeatureflagsState
    ], {
      developmentMode: !environment.production,
    }),
    NgxsReduxDevtoolsPluginModule.forRoot(),
    MessageModule,
    ToastModule,
    ConfirmDialogModule,
    ProgressSpinnerModule,
    DialogModule,
    PrintStartcodesLayoutComponent,
    DoeToastComponent,
  ],
  providers: [
    { provide: LOCALE_ID, useValue: 'nl' },
    provideAppInitializer(() => {
      const initializerFn = appSettingsInitializerFactory(inject(AppSettingsService), inject(Store));
      return initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn = appInitializerFactory(inject(TranslateService));
      return initializerFn();
    }),
    MessageService,
    ConfirmationService,
    {
      provide: TitleStrategy,
      useClass: TemplatePageTitleStrategy,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: BffForbiddenInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useFactory: (captureModeService: CaptureModeService, captureStorage: CaptureStorage) => {
        if (captureModeService.isEnabled) {
          return new CaptureInterceptor(captureStorage);
        }
        // Return a passthrough interceptor when capture mode is disabled
        return {
          intercept: (req: HttpRequest<unknown>, next: HttpHandler) => next.handle(req)
        };
      },
      deps: [CaptureModeService, CaptureStorage],
      multi: true,
    },
    providePrimeNG({
      license: environment.PrimeNGLicenseToken,
      ripple: true,
      theme: {
        preset: environment.variant === AppVariants.DOE ? CitoDoePreset : CitoOcwPreset,
        options: {
          darkModeSelector: false,
        },
      },
    }),
  ],
  bootstrap: [AppComponent],
  exports: [TranslateModule],
})
export class AppModule {}
